// src/page/SpotEdit.js
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import { GENRE_OPTIONS } from '../constants/spotOptions';
import { numOrNull, toISO } from '../utils/formHelper';
import { QRcodeIssuance, revokeObjectUrlSafe } from '../utils/qr';

export default function SpotEdit() {
  useAuthGuard();
  const { spotID } = useParams();
  const navigate = useNavigate();

  // ---- fields used on this screen ----
  const [spotName, setSpotName] = useState('');
  const [spotDescription, setSpotDescription] = useState('');
  const [publishedFlag, setPublishedFlag] = useState('0'); // '0' | '1'
  const [publicRange, setPublicRange] = useState('');      // "YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
  const [displayOrder, setDisplayOrder] = useState('');
  const [genre, setGenre] = useState([]);                  // CSV (array in UI)
  const [spotLink, setSpotLink] = useState('');
  const [linkOpenMode, setLinkOpenMode] = useState('1'); // '1': WebView, '2': Browser
  const [activityPoint, setActivityPoint] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  // ---- QR (coupon/action) ----
  const [evpfStoreSerialCode, setEvpfStoreSerialCode] = useState('');
  const [couponQrUrl, setCouponQrUrl] = useState('');
  const [couponQrName, setCouponQrName] = useState('');
  const [actionQrUrl, setActionQrUrl] = useState('');
  const [actionQrName, setActionQrName] = useState('');

  // ---- image (single) ----
  const [imageFile, setImageFile] = useState(null);        // File | null
  const [imagePreview, setImagePreview] = useState('');    // object URL or server URL
  const [serverImagePath, setServerImagePath] = useState(''); // persisted path (DB: spot_image)
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ---- UI state ----
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ---- messages ----
  const MSG = {
    errSpotName: 'スポット名は必須です',
    errPublishedFlag: '公開設定を選択してください',
    errPublishedRange: '公開期間は必須です（開始・終了を選択してください）',
    errOrderRequired: '表示順は必須です',
    errOrder: '表示順は整数で入力してください',
    errGenre: 'ジャンルを1つ以上選択してください',
    errLatitude: '緯度は-90～90の範囲で入力してください',
    errLongitude: '経度は-180～180の範囲で入力してください',
    saved: '更新しました。',
    deleted: '削除しました。',
    loadError: '読み込みに失敗しました',
    updateError: '更新に失敗しました。',
    deleteError: '削除に失敗しました。',
    uploadError: '画像のアップロードに失敗しました。'
  };

  // ---- download helpers ----
  const safeName = (s) => (s || '')
    .toString()
    .replace(/[^A-Za-z0-9._-]/g, '_')
    .replace(/^_+|_+$/g, '');
  const downloadBlobUrl = (url, filename) => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'qr.png';
    if (typeof a.download === 'undefined') a.target = '_blank';
    a.click();
  };

  // ---- date utils ----
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (s) => {
    if (!s) return '';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // ---- validation helpers ----
  const RANGE_RE = /^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2} ~ \d{4}\/\d{2}\/\d{2} \d{2}:\d{2}$/;
  const parseDT = (s) => {
    const [d, t] = s.trim().split(' ');
    const [Y, M, D] = d.split('/').map(Number);
    const [h, m] = t.split(':').map(Number);
    const x = new Date(Y, M - 1, D, h, m);
    return Number.isNaN(x.getTime()) ? null : x;
  };
  const rangeError = (range) => {
    if (!range || !RANGE_RE.test(range)) return MSG.errPublishedRange;
    const [a, b] = range.split('~').map(s => s.trim());
    const s = parseDT(a), e = parseDT(b);
    if (!s || !e || s > e) return '公開開始は公開終了以前である必要があります';
    return null;
  };

  const [errors, setErrors] = useState({});
  const invalid = (k) => (errors[k] ? 'is-invalid' : '');
  const feedback = (k) => (errors[k] ? <div className="invalid-feedback d-block">{errors[k]}</div> : null);

  const validate = () => {
    const err = {};
    if (!spotName.trim()) err.spotName = MSG.errSpotName;
    if (!['0', '1'].includes(publishedFlag)) err.publishedFlag = MSG.errPublishedFlag;

    const prErr = rangeError(publicRange); // unconditional
    if (prErr) err.publishedRange = prErr;

    // [CHANGE] 表示順は任意入力に。未入力はOK、入力があれば整数チェックのみ
    const v = String(displayOrder).trim();
    if (v !== '' && !/^-?\d+$/.test(v)) err.displayOrder = MSG.errOrder;

    if (!Array.isArray(genre) || genre.length < 1) err.genre = MSG.errGenre;

    if (latitude !== '' && (Number.isNaN(Number(latitude)) || Number(latitude) < -90 || Number(latitude) > 90)) {
      err.latitude = MSG.errLatitude;
    }
    if (longitude !== '' && (Number.isNaN(Number(longitude)) || Number(longitude) < -180 || Number(longitude) > 180)) {
      err.longitude = MSG.errLongitude;
    }

    setErrors(err);
    return err;
  };

  const scrollToFirstError = (errObj) => {
    const keys = Object.keys(errObj);
    if (!keys.length) return;
    const first = keys[0];
    const node =
      document.querySelector(`[data-error-key="${first}"]`) ||
      document.querySelector(`[name="${first}"]`);
    if (node?.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // ---- flatpickr ----
  const fpRef = useRef(null);
  useEffect(() => {
    if (!window.flatpickr) return;

    const fmt2 = (d) =>
      `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;

    fpRef.current = window.flatpickr('#public_range', {
      mode: 'range',
      enableTime: true,
      time_24hr: true,
      dateFormat: 'Y/m/d H:i',
      locale: window.flatpickr.l10ns?.ja || 'ja',
      onChange: (ds) => {
        if (ds.length === 2) {
          setPublicRange(`${fmt2(ds[0])} ~ ${fmt2(ds[1])}`);
        } else if (ds.length === 0) {
          setPublicRange('');
        }
      }
    });

    return () => { try { fpRef.current?.destroy(); } catch { /* noop */ } };
  }, []);

  // [ADD] クリア処理（入力値とピッカーの両方をクリア）
  const clearPublicRange = () => {
    setPublicRange('');
    try { fpRef.current?.clear(); } catch { /* noop */ }
  };

  // reflect current value -> picker (after API load)
  useEffect(() => {
    if (!fpRef.current) return;
    if (!publicRange) { try { fpRef.current.clear(); } catch { } return; }
    if (publicRange.includes('~')) {
      const [a, b] = publicRange.split('~').map(s => s.trim());
      fpRef.current.setDate([a, b], true);
    }
  }, [publicRange]);

  // ---- file URL helper (adjust to your server's public URL) ----
  // Example: if download URL is `${API_BASE_URL}/files/<path>`
  const fileURL = (p) => (p ? `${API_BASE_URL.replace(/\/$/, '')}/files/${encodeURIComponent(p)}` : '');
  const isBlobUrl = (u) => {
    try { return new URL(u).protocol === 'blob:'; } catch { return false; }
  };

  // ---- image helpers ----
  const fileInputRef = useRef(null);

  const setSingleImage = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      alert('画像ファイルを選択してください');
      return;
    }
    if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    // keep serverImagePath as-is until save; final decision happens on submit
  };

  const handlePickClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setSingleImage(file);
    e.target.value = ''; // keep only one file
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setSingleImage(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };

  const clearImage = () => {
    if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview('');
    setServerImagePath(''); // explicit remove
  };

  useEffect(() => {
    return () => { if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview); };
  }, [imagePreview]);

  // ---- fetch detail (only needed fields) ----
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_BASE_URL}/v1/spot/${encodeURIComponent(spotID)}`, {
          headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const s = data.spot || {};

        setSpotName(s.spotName || '');
        setSpotDescription(s.spotDescription || '');
        setDisplayOrder(typeof s.displayOrder === 'number' ? String(s.displayOrder) : (s.displayOrder || ''));
        setGenre((s.spotGenre || '').split(',').filter(Boolean));
        setPublishedFlag(s.publishedFlag ? '1' : '0');
        setSpotLink(s.spotLink || '');
        setLinkOpenMode((s.launcherMode ?? '1').toString() === '2' ? '2' : '1');
        setActivityPoint(
          typeof s.activityPoint === 'number' && !Number.isNaN(s.activityPoint) ? String(s.activityPoint) : ''
        );
        setLatitude(
          typeof s.latitude === 'number' && !Number.isNaN(s.latitude) ? String(s.latitude) : ''
        );
        setLongitude(
          typeof s.longitude === 'number' && !Number.isNaN(s.longitude) ? String(s.longitude) : ''
        );

        const start = fmt(s.publishedStartDate);
        const end = fmt(s.publishedEndDate);
        setPublicRange(start && end ? `${start} ~ ${end}` : '');

        // --- NEW: preset server-stored image (DB column: spot_image) ---
        const persisted = s.spotImage || s.imagePath || ''; // accept either key
        setServerImagePath(persisted);
        if (persisted) {
          setImagePreview(fileURL(persisted)); // show server image as preview
        } else {
          setImagePreview('');
        }
        // クーポン用コード（APIのキー差異を吸収）
        setEvpfStoreSerialCode(s.evpfStoreSerialCode || s.evpf_store_serial_code || '');

      } catch (e) {
        if (e.name !== 'AbortError') setError(MSG.loadError);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [spotID]);

  // ---- QR: クーポン用（evpf_store_serial_code） ----
  useEffect(() => {
    // 前のURLを掃除
    revokeObjectUrlSafe(couponQrUrl);
    if (evpfStoreSerialCode) {
      try {
        const url = QRcodeIssuance(evpfStoreSerialCode, 256);
        setCouponQrUrl(url);
        const base = safeName(evpfStoreSerialCode) || 'coupon';
        setCouponQrName(`${base}.png`);
      } catch {
        setCouponQrUrl('');
        setCouponQrName('');
      }
    } else {
      setCouponQrUrl('');
      setCouponQrName('');
    }
    return () => revokeObjectUrlSafe(couponQrUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evpfStoreSerialCode]);

  // ---- QR: 行動ポイント用（spot_id） ----
  // ---- local helpers ----
  const yyyymmddhhmmss = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    const Y = d.getFullYear();
    const M = pad(d.getMonth() + 1);
    const D = pad(d.getDate());
    const h = pad(d.getHours());
    const m = pad(d.getMinutes());
    const s = pad(d.getSeconds());
    return `${Y}${M}${D}${h}${m}${s}`;
  };
  useEffect(() => {
    revokeObjectUrlSafe(actionQrUrl);
    if (spotID) {
      try {
        const now = new Date();
        const payload = `${spotID}${yyyymmddhhmmss(now)}`;
        const url = QRcodeIssuance(payload, 256);
        setActionQrUrl(url);
        const base = safeName(`${spotID}-${yyyymmddhhmmss(now)}`) || 'action';
        setActionQrName(`${base}.png`);
      } catch { setActionQrUrl(''); }
    } else {
      setActionQrUrl('');
      setActionQrName('');
    }
    return () => revokeObjectUrlSafe(actionQrUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotID]);

  // ---- upload image to /v1/file (path=spot/<spotID>/) ----
  const uploadImageIfNeeded = async () => {
    if (!imageFile) return null; // nothing to upload
    setUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const fd = new FormData();
      fd.append('file', imageFile, imageFile.name);
      fd.append('path', `spot/${spotID}/`); // saved as path + filename on the server

      const res = await fetch(`${API_BASE_URL}/v1/file`, {
        method: 'POST',
        headers: { Authorization: token ? `Bearer ${token}` : '' },
        body: fd // note: do not set Content-Type here
      });

      if (!res.ok) {
        let msg = `画像アップロードに失敗しました (HTTP ${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
        throw new Error(msg);
      }

      // expected JSON: { path: "spot/<spotID>/<filename>" } or string
      let resultPath = null;
      try {
        const j = await res.json();
        resultPath = j?.path || j?.result || null;
      } catch {
        // if server returns raw text
        resultPath = await res.text();
      }
      return resultPath;
    } finally {
      setUploading(false);
    }
  };

  // ---- submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate(); // only on click "更新"
    if (Object.keys(err).length) {
      scrollToFirstError(err);
      return;
    }

    try {
      setSaving(true);

      // 1) Upload image first (if any)
      let uploadedImagePath = null;
      if (imageFile) {
        uploadedImagePath = await uploadImageIfNeeded();
        if (!uploadedImagePath) {
          throw new Error(MSG.uploadError);
        }
      }

      // 2) Update spot
      const [lhs, rhs] = publicRange.split('~').map(s => s.trim());
      // Decide which image path to send:
      // - if user selected a new file, use uploaded path
      // - else keep existing persisted path (unless cleared)
      const finalImagePath = uploadedImagePath ?? serverImagePath ?? '';
      const payload = {
        spot: {
          spotId: spotID,
          spotName,
          spotDescription,
          displayOrder: numOrNull(displayOrder, true),
          spotGenre: (genre || []).join(','),
          publishedFlag: publishedFlag === '1',
          publishedStartDate: toISO(lhs),
          publishedEndDate: toISO(rhs),
          spotImage: finalImagePath,
          spotLink,
          launcherMode: linkOpenMode,
          activityPoint: numOrNull(activityPoint, true),
          latitude: latitude !== '' ? parseFloat(latitude) : null,
          longitude: longitude !== '' ? parseFloat(longitude) : null
        }
      };

      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE_URL}/v1/spot/${encodeURIComponent(spotID)}`, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        let msg = `更新に失敗しました (HTTP ${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
        throw new Error(msg);
      }

      alert(MSG.saved);
      navigate('/spot');
    } catch (e2) {
      alert(e2?.message || MSG.updateError);
    } finally {
      setSaving(false);
    }
  };

  // ---- delete ----
  const handleDelete = async () => {
    if (deleting) return;
    if (!window.confirm('本当に削除しますか？この操作は取り消せません。')) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE_URL}/v1/spot/${encodeURIComponent(spotID)}`, {
        method: 'DELETE',
        headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' }
      });
      if (!res.ok) {
        let msg = `削除に失敗しました (HTTP ${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
        throw new Error(msg);
      }
      alert(MSG.deleted);
      navigate('/spot');
    } catch (e) {
      alert(e?.message || MSG.deleteError);
    } finally {
      setDeleting(false);
    }
  };

  const savingOrUploading = saving || uploading || deleting;

  return (
    <Layout>
      <>
        <nav className="mb-3" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><a href="/spot">スポット管理</a></li>
            <li className="breadcrumb-item active">編集</li>
          </ol>
        </nav>

        {loading && <div className="text-body-secondary mt-3">読み込み中...</div>}
        {!loading && error && <div className="text-danger mt-3">{error}</div>}

        <form className="mb-9" onSubmit={handleSubmit}>
          <div className="row g-3 flex-between-end mb-5">
            <div className="col-auto"><h2 className="mb-2">スポット編集</h2></div>
            <div className="col-auto">
              <button className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0" type="button" onClick={() => navigate('/spot')} disabled={savingOrUploading}>キャンセル</button>
              <button className="btn btn-primary me-2 mb-2 mb-sm-0" type="submit" disabled={savingOrUploading}>
                {uploading ? '画像アップロード中...' : (saving ? '保存中...' : '更新')}
              </button>
              <button className="btn btn-danger mb-2 mb-sm-0" type="button" onClick={handleDelete} disabled={savingOrUploading}>
                {deleting ? '削除中...' : '削除'}
              </button>
            </div>
          </div>

          <div className="row g-5">

            <div className="col-12 col-xl-8">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">スポットID</h4>
                    <input className="form-control" type="text" value={spotID} disabled />
                  </div>
                </div>
              </div>
              <div className={`mb-4 ${invalid('spotName')}`} data-error-key="spotName">
                <h4 className="mb-3">スポット名<span className="text-danger">*</span></h4>
                <input
                  className={`form-control ${invalid('spotName')}`}
                  type="text"
                  name="spotName"
                  value={spotName}
                  onChange={(e) => setSpotName(e.target.value)}
                  disabled={savingOrUploading}
                />
                {feedback('spotName')}
              </div>
              <div className="mb-4">
                <h4 className="mb-3">内容</h4>
                <textarea
                  className="form-control"
                  name="content"
                  rows={4}
                  value={spotDescription}
                  onChange={(e) => setSpotDescription(e.target.value)}
                  disabled={savingOrUploading}
                />
              </div>

              <div className={`mb-4 ${invalid('spotName')}`}>
                <h4 className="mb-3">関連リンク</h4>
                <div className="input-group">
                  <input
                    className="form-control"
                    type="url"
                    value={spotLink}
                    onChange={(e) => setSpotLink(e.target.value)}
                    disabled={savingOrUploading}
                  />
                  <button
                    type="button"
                    className={`btn ${linkOpenMode === '1' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLinkOpenMode('1')}
                    disabled={savingOrUploading}
                    aria-pressed={linkOpenMode === '1'}
                  >
                    WebView
                  </button>
                  <button
                    type="button"
                    className={`btn ${linkOpenMode === '2' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setLinkOpenMode('2')}
                    disabled={savingOrUploading}
                    aria-pressed={linkOpenMode === '2'}
                  >
                    ブラウザ
                  </button>
                </div>
              </div>


              <div className="row">
                <div className="col-md-6">
                  <div className="card mb-4">
                    <div className="card-body">
                      <h4 className="mb-3">クーポン用QRコード</h4>
                      {couponQrUrl ? (
                        <img
                          src={couponQrUrl}
                          alt="クーポン用QRコード"
                          className="border rounded"
                          style={{ width: 180, height: 180 }}
                        />
                      ) : (
                        <div className="text-body-secondary small">クーポン用コードが未設定です</div>
                      )}
                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => downloadBlobUrl(couponQrUrl, couponQrName)}
                          disabled={!couponQrUrl}
                        >ダウンロード</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card mb-4">
                    <div className="card-body">
                      <h4 className="mb-3">行動ポイント用QRコード</h4>
                      {actionQrUrl ? (
                        <img src={actionQrUrl} alt="行動ポイント用QRコード" className="border rounded" style={{ width: 180, height: 180 }} />
                      ) : (
                        <div className="text-body-secondary small">spot_id が取得できません</div>
                      )}
                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => downloadBlobUrl(actionQrUrl, actionQrName)}
                          disabled={!actionQrUrl}
                        >ダウンロード</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <div className="col-12 col-xl-4">
              <div className="row g-2">
                <div className="col-12">
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="mb-4" data-error-key="publishedFlag">
                        <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                        <div className="d-flex align-items-center gap-3">
                          <label className="mb-0">
                            <input
                              type="radio"
                              name="publish_status"
                              value="0"
                              checked={publishedFlag === '0'}
                              onChange={(e) => setPublishedFlag(e.target.value)}
                              disabled={savingOrUploading}
                            /> 非公開
                          </label>
                          <label className="mb-0">
                            <input
                              type="radio"
                              name="publish_status"
                              value="1"
                              checked={publishedFlag === '1'}
                              onChange={(e) => setPublishedFlag(e.target.value)}
                              disabled={savingOrUploading}
                            /> 公開
                          </label>
                        </div>
                        {feedback('publishedFlag')}
                      </div>

                      <div className={`mb-4 ${invalid('publishedRange')}`} data-error-key="publishedRange">
                        <h5 className="mb-0 text-body-highlight me-2">公開期間<span className="text-danger">*</span></h5>

                        <div className="input-group">
                          <input
                            className={`form-control ${invalid('publishedRange')}`}
                            type="text"
                            name="public_range"
                            id="public_range"
                            placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                            value={publicRange}
                            readOnly
                          />
                          <span
                            className="input-group-text"
                            role="button"
                            tabIndex={0}
                            onClick={clearPublicRange}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearPublicRange(); }}
                            aria-label="クリア"
                            title="クリア"
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                          >
                            ×
                          </span>
                        </div>

                        {feedback('publishedRange')}
                      </div>

                      <div className={`mb-4 ${invalid('displayOrder')}`} data-error-key="displayOrder">
                        <h5 className="mb-0 text-body-highlight me-2">表示順</h5>
                        <input
                          className={`form-control ${invalid('displayOrder')}`}
                          type="number"
                          value={displayOrder}
                          onChange={(e) => setDisplayOrder(e.target.value)}
                          disabled={savingOrUploading}
                        />
                        {feedback('displayOrder')}
                      </div>

                      <div className={`mb-4 `}>
                        <h5 className="mb-0 text-body-highlight me-2">行動ポイント</h5>
                        <input
                          className="form-control"
                          type="number"
                          value={activityPoint}
                          onChange={(e) => setActivityPoint(e.target.value)}
                          disabled={savingOrUploading}
                        />
                      </div>
                      <div className="mb-4" data-error-key="latitude">
                        <h5 className="mb-0 text-body-highlight me-2">緯度</h5>
                        <input
                          className={`form-control ${invalid('latitude')}`}
                          type="number"
                          name="latitude"
                          step="0.00000001"
                          min="-90"
                          max="90"
                          value={latitude}
                          onChange={(e) => setLatitude(e.target.value)}
                          disabled={savingOrUploading}
                          placeholder="例: 37.91234567"
                        />
                        {feedback('latitude')}
                      </div>
                      <div className="mb-4" data-error-key="longitude">
                        <h5 className="mb-0 text-body-highlight me-2">経度</h5>
                        <input
                          className={`form-control ${invalid('longitude')}`}
                          type="number"
                          name="longitude"
                          step="0.00000001"
                          min="-180"
                          max="180"
                          value={longitude}
                          onChange={(e) => setLongitude(e.target.value)}
                          disabled={savingOrUploading}
                          placeholder="例: 139.02345678"
                        />
                        {feedback('longitude')}
                      </div>

                    </div>
                  </div>
                </div>

                {/* Tags */}
                <div className="col-12">
                  <div className="card mb-3" data-error-key="genre">
                    <div className="card-body">
                      <div className={`mb-1 ${invalid('genre')}`}>
                        <h5 className="mb-2 text-body-highlight">ジャンル<span className="text-danger">*</span></h5>
                        {GENRE_OPTIONS.map(([val, label]) => (
                          <label key={val} className="me-3">
                            <input
                              type="checkbox"
                              checked={genre.includes(val)}
                              onChange={(e) =>
                                setGenre(g => e.target.checked ? [...new Set([...g, val])] : g.filter(v => v !== val))
                              }
                              disabled={savingOrUploading}
                            /> {label}
                          </label>
                        ))}
                      </div>
                      {feedback('genre')}
                    </div>
                  </div>
                </div>

                {/* Spot image (single) */}
                <div className="col-12">
                  <div className="card mb-3">
                    <div className="card-body">
                      <h4 className="card-title mb-3">スポット画像</h4>

                      {!imagePreview && (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={handlePickClick}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePickClick(); }}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onDragEnter={handleDragOver}
                          onDragLeave={handleDragLeave}
                          className={`border border-dashed rounded-3 p-4 text-center ${isDragOver ? 'bg-body-tertiary' : ''}`}
                          style={{ cursor: 'pointer' }}
                          aria-label="画像をドラッグ&ドロップ、またはクリックして選択"
                        >
                          <p className="mb-2">ここに画像をドラッグ＆ドロップ</p>
                          <p className="mb-2">または</p>
                          <button type="button" className="btn btn-outline-primary" disabled={savingOrUploading}>ファイルを選択</button>
                          <p className="text-body-tertiary mt-2 mb-0">対応形式: 画像ファイル（1枚まで）</p>
                        </div>
                      )}

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        disabled={savingOrUploading}
                      />

                      {imagePreview && (
                        <div className="d-flex align-items-start gap-3">
                          <img
                            src={imagePreview}
                            alt="プレビュー"
                            className="rounded border"
                            style={{ width: 160, height: 160, objectFit: 'cover' }}
                          />
                          <div className="flex-grow-1">
                            <div className="mb-2 small text-body-secondary">
                              {imageFile?.name} {imageFile ? `(${Math.round(imageFile.size / 1024)} KB)` : (serverImagePath ? `(サーバー保存: ${serverImagePath})` : '')}
                            </div>
                            <div className="d-flex gap-2">
                              <button type="button" className="btn btn-outline-secondary" onClick={handlePickClick} disabled={savingOrUploading}>
                                画像を変更
                              </button>
                              <button type="button" className="btn btn-outline-danger" onClick={clearImage} disabled={savingOrUploading}>
                                画像を削除
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </form>
      </>
    </Layout>
  );
}
